// src/components/AssistantWidget.js
// UPDATED: 2025-05-21 - Further refined useEffect and useCallback dependencies to prevent duplicates and typing lag.
// UPDATED: 2025-05-22 - Fixed deep_patient_context payload and added guard for uncontrolled display.
// FURTHER UPDATED: 2025-05-22 - Ensured correct direct usage of activeDeepContext prop for payload.
// CORRECTION: 2025-05-22 - Imported alpha for MUI styling.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, IconButton, List, ListItem,
    ListItemText, CircularProgress, Alert, Tooltip, Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles'; // <<<--- ADD THIS IMPORT
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

import authFetch from '../utils/authFetch';
import logger from '../utils/logger';
import { useNavigate } from 'react-router-dom';

const renderAssistantResponse = (text) => {
    // ... (render logic remains the same)
    if (text && typeof text === 'string' && text.startsWith('Found') && text.includes('\n  -')) {
        const lines = text.split('\n'); const header = lines[0]; const items = lines.slice(1).filter(line => line.trim().startsWith('- ')); const footer = lines.filter(line => !line.trim().startsWith('- ') && line !== header).join('\n');
        return ( <> <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>{header}</Typography> <List dense sx={{ py: 0, pl: 1 }}> {items.map((item, index) => ( <ListItem key={index} sx={{ py: 0.2, px: 0 }}> <ListItemText primary={item.substring(3)} primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'inherit', fontSize: '0.85rem', lineHeight: 1.4 } }} /> </ListItem> ))} </List> {footer && <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{footer}</Typography>} </> );
    }
    if (text && typeof text === 'string' && text.includes('```sql')) {
        const parts = text.split('```sql'); return ( <> <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{parts[0]}</Typography> {parts[1] && ( <Paper elevation={0} sx={{ p: 1, my: 1, bgcolor: 'grey.100', overflowX: 'auto' }}> <Typography component="pre" variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}> {parts[1].replace(/```/g,'').trim()} </Typography> </Paper> )} </> )
    }
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text || ''}</Typography>;
};

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;
if (recognition) {
    recognition.continuous = false;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
} else {
    logger.warn("AssistantWidget: SpeechRecognition API not supported by this browser.");
}
const synthesis = window.speechSynthesis;
if (!synthesis) {
    logger.warn("AssistantWidget: SpeechSynthesis API not supported by this browser.");
}

function AssistantWidget({
    apiEndpoint = '/api/assistant/query',
    tenantId,
    patientId: patientIdProp,
    episodeId: episodeIdProp,
    contextType,
    relatedData,
    initialMessage,
    activeDeepContext, 
    assistanceContext = null, //  <-- Add this prop
    externallyControlledOpen,
}) {
    logger.debug("AssistantWidget: Prop activeDeepContext upon render/prop change:", activeDeepContext, "Type:", typeof activeDeepContext);
    
    
    useEffect(() => {
        // logger.debug("AW PROPS RECEIVED: externallyControlledOpen:", externallyControlledOpen, "activeDeepContext IS OBJECT:", typeof activeDeepContext === 'object' && activeDeepContext !== null);
    }, [externallyControlledOpen, tenantId, patientIdProp, episodeIdProp, contextType, initialMessage, activeDeepContext, relatedData]);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const messagesEndRef = useRef(null);
    const navigate = useNavigate();

    const [isListening, setIsListening] = useState(false);
    const [isSpeakerOn, setIsSpeakerOn] = useState(false);
    const recognitionRef = useRef(recognition);
    const synthRef = useRef(synthesis);
    const utteranceRef = useRef(null);

    const params = useParams();
    const patientIdFromUrl = params.patientUserId;
    const episodeIdFromUrl = params.episodeId;

    const patientContext = patientIdProp || patientIdFromUrl;
    const episodeContext = episodeIdProp || episodeIdFromUrl;

    const initialCallMadeForPropsRef = useRef('');

    const scrollToBottom = useCallback(() => {
        setTimeout(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, 100);
    }, []);

    useEffect(() => {
        if (externallyControlledOpen === true) {
            scrollToBottom();
        }
    }, [messages, externallyControlledOpen, scrollToBottom]);

    const speakText = useCallback((textToSpeak) => {
        if (!isSpeakerOn || !synthRef.current || !textToSpeak) return;
        if (synthRef.current.speaking) { synthRef.current.cancel(); }
        let cleanText = typeof textToSpeak === 'string' ? textToSpeak : "Response not speakable.";
        cleanText = cleanText.replace(/Error: /gi,'Error: ');
        logger.debug(`TTS: Speaking: "${cleanText.substring(0,100)}..."`);
        utteranceRef.current = new SpeechSynthesisUtterance(cleanText);
        utteranceRef.current.onerror = (event) => { logger.error('SpeechSynthesis Error:', event.error); };
        synthRef.current.speak(utteranceRef.current);
    }, [isSpeakerOn]);

    useEffect(() => {
        if (externallyControlledOpen === true && isSpeakerOn && messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.role === 'assistant' && !lastMessage.text.toLowerCase().startsWith("error:")) {
                speakText(lastMessage.text);
            }
        }
        return () => { if (synthRef.current && synthRef.current.speaking) { synthRef.current.cancel(); } };
    }, [messages, isSpeakerOn, speakText, externallyControlledOpen]);

    const handleSend = useCallback(async (isInitialQuery = false) => {
        logger.info(`AssistantWidget: handleSend called. isInitial: ${isInitialQuery}`);

        const queryText = isInitialQuery
            ? (initialMessage || relatedData?.queryPrompt || contextType || '')
            : input;

        if (!queryText.trim()) {
            logger.warn(`AssistantWidget: Send attempt with effectively empty queryText. isInitial: ${isInitialQuery}, queryText: '${queryText}'`);
            if (!isInitialQuery) setError("Please type a message or query.");
            setLoading(false);
            return;
        }

        if (!isInitialQuery) {
            const userMessage = { role: 'user', text: queryText };
            setMessages(prev => [...prev, userMessage]);
            setInput('');
        }

        setLoading(true);
        setError('');


        let deepContextForPayload;
        if (typeof activeDeepContext === 'boolean') {
            // If, for some reason, a boolean (like false) is received, force it to null
            // as the backend expects an object or null.
            logger.warn(`AssistantWidget: activeDeepContext prop was a boolean (${activeDeepContext}). Forcing deep_patient_context to null in payload.`);
            deepContextForPayload = null;
        } else {
            // If it's not a boolean, it should be the actual object or already null.
            deepContextForPayload = activeDeepContext;
        }


        const requestPayload = {
            query: queryText,
            episode_id: episodeContext,
            patient_id: patientContext,
            tenant_id: tenantId,
            context_type: contextType,
            related_data: relatedData,
            deep_patient_context: deepContextForPayload,
            history: messages,
            assistanceContext: assistanceContext  
        };

        logger.debug("AssistantWidget: Sending Request Payload:", JSON.stringify(requestPayload, (key, value) =>
            key === 'deep_patient_context' && value ? '{...deepContextPresent...}' : value, 2
        ));

        try {
            const data = await authFetch(apiEndpoint, { method: 'POST', body: requestPayload });
            logger.info("AssistantWidget API response:", data);
            let assistantResponseText = data?.response || 'Sorry, I could not process that. Please try again.';
            let navigationSuggestion = data?.navigation_suggestion;

            if (isInitialQuery && messages.length === 1 && initialMessage && messages[0].text.includes(String(initialMessage).substring(0,50))) {
                setMessages([{ role: 'assistant', text: assistantResponseText, navigation_suggestion: navigationSuggestion }]);
            } else {
                setMessages(prevMessages => [...prevMessages, { role: 'assistant', text: assistantResponseText, navigation_suggestion: navigationSuggestion }]);
            }
            
            if (navigationSuggestion && navigationSuggestion.should_navigate && navigate) {
                const navPath = `/${navigationSuggestion.page}/${navigationSuggestion.patient_id}${navigationSuggestion.anchor ? '#' + navigationSuggestion.anchor : ''}`;
                navigate(navPath);
            }
        } catch (err) {
            logger.error(`Error sending message to ${apiEndpoint}:`, err);
            const detail = err?.data?.detail || err?.response?.data?.detail || err?.message || 'An unexpected error occurred.';
            const errorText = typeof detail === 'string' ? detail : JSON.stringify(detail);
            const errorResponse = { role: 'assistant', text: `Error: ${errorText}` };
            if (isInitialQuery && messages.length === 1 && initialMessage && messages[0].text.includes(String(initialMessage).substring(0,50))) {
                setMessages([errorResponse]);
            } else {
                setMessages(prevMessages => [...prevMessages, errorResponse]);
            }
            setError(errorText);
        } finally {
            setLoading(false);
        }
    }, [
        input, initialMessage, contextType, 
        episodeContext, patientContext, tenantId, relatedData, 
        activeDeepContext, 
        apiEndpoint, navigate,
        assistanceContext, 
    ]);


    useEffect(() => {
        const currentPropsSignatureForInitialCall = `${initialMessage}-${contextType}-${patientContext}-${episodeContext}-${typeof activeDeepContext === 'object' && activeDeepContext !== null}`;
        
        if (externallyControlledOpen === true) {
            let displayMessageToShow = initialMessage;
            if (!displayMessageToShow) {
                const defaultText = 'Hello! How can I assist you today?';
                let contextChipInfo = '';
                 if (patientContext && episodeContext) { contextChipInfo = ` (Context: Pt ${String(patientContext).substring(0, 6)}... / Ep ${String(episodeContext).substring(0, 6)}...)`; }
                 else if (patientContext) { contextChipInfo = ` (Context: Pt ${String(patientContext).substring(0, 6)}...)`; }
                 else if (episodeContext) { contextChipInfo = ` (Context: Ep ${String(episodeContext).substring(0, 6)}...)`; }
                 else if (contextType === 'physician_general_assistant') { contextChipInfo = ' (General Assistant Mode)';}
                displayMessageToShow = defaultText + contextChipInfo;
            }

            const firstMessageIsInitialDisplay = messages.length > 0 && messages[0].role === 'assistant' && messages[0].text.includes(String(displayMessageToShow).substring(0,50));

            if (messages.length === 0 || !firstMessageIsInitialDisplay) {
                logger.info(`AssistantWidget: Dialog is open. Setting initial display message: "${String(displayMessageToShow).substring(0,100)}..."`);
                setMessages([{ role: 'assistant', text: displayMessageToShow }]);
            }
            setError('');

            if (initialCallMadeForPropsRef.current !== currentPropsSignatureForInitialCall &&
                contextType && contextType !== 'physician_general_assistant' &&
                initialMessage && String(initialMessage).trim() !== "" &&
                activeDeepContext && typeof activeDeepContext === 'object' && activeDeepContext !== null) { 
                logger.info(`AssistantWidget: Triggering initial AI query for contextType: ${contextType} with deep context.`);
                initialCallMadeForPropsRef.current = currentPropsSignatureForInitialCall;
                handleSend(true);
            } else if (initialCallMadeForPropsRef.current === currentPropsSignatureForInitialCall) {
                logger.debug(`AssistantWidget: Initial call already made for current props signature: ${currentPropsSignatureForInitialCall}`);
            } else {
                logger.info(`AssistantWidget: Not making initial AI query (contextType: ${contextType}, initialMessage: "${initialMessage}", activeDeepContext is object: ${typeof activeDeepContext === 'object' && activeDeepContext !== null})`);
                if(loading) setLoading(false);
            }
        } else if (externallyControlledOpen === false) {
            if (initialCallMadeForPropsRef.current !== '') {
                logger.info("AssistantWidget: Dialog closed by parent, resetting initialCallMade flag and clearing state.");
                initialCallMadeForPropsRef.current = '';
                setMessages([]);
                setInput('');
                setError('');
            }
            if (synthRef.current && synthRef.current.speaking) synthRef.current.cancel();
            if (recognitionRef.current && isListening) {
                if(recognitionRef.current.stop) recognitionRef.current.stop();
                setIsListening(false);
            }
        }
    }, [externallyControlledOpen, initialMessage, contextType, patientContext, episodeContext, activeDeepContext, handleSend, loading, messages]); // Added messages to re-evaluate initial message


     useEffect(() => {
        const currentRecognition = recognitionRef.current;
        if (!currentRecognition) return;
        const handleResult = (event) => {
            const transcript = event.results[event.results.length - 1][0].transcript.trim();
            logger.debug(`Speech recognized (final): '${transcript}'`);
            setInput(transcript);
            setIsListening(false);
        };
        const handleErrorEvent = (event) => { 
            logger.error('SpeechRecognition Error:', event.error, 'Message:', event.message);
            let errorMsgText = event.error;
            if (event.error === 'no-speech') { errorMsgText = 'No speech detected.'; }
            else if (event.error === 'audio-capture') { errorMsgText = 'Microphone error.'; }
            else if (event.error === 'not-allowed') { errorMsgText = 'Mic access denied.'; }
            else { errorMsgText = `Mic error: ${errorMsgText}`; }
            setError(errorMsgText);
            setIsListening(false);
        };
        const handleEnd = () => {
            logger.debug('Speech recognition ended.');
            if (isListening) {
                setIsListening(false);
            }
        };
        currentRecognition.onresult = handleResult;
        currentRecognition.onerror = handleErrorEvent;
        currentRecognition.onend = handleEnd;
        return () => {
            if (currentRecognition) {
                currentRecognition.onresult = null;
                currentRecognition.onerror = null;
                currentRecognition.onend = null;
                if (isListening && typeof currentRecognition.stop === 'function') {
                    try { logger.debug("STT Cleanup: Attempting to stop recognition from useEffect cleanup."); currentRecognition.stop(); }
                    catch(e){ logger.warn("Error stopping recognition on STT useEffect cleanup:", e); }
                }
            }
             if (isListening) setIsListening(false);
        };
    }, [isListening]); 

    const handleToggleMic = () => {
        if (!recognitionRef.current) { setError("Speech recognition not supported."); return; }
        navigator.mediaDevices.getUserMedia({ audio: true })
             .then(() => {
                 if (isListening) {
                     logger.debug("Stopping mic manually.");
                     recognitionRef.current.stop();
                 } else {
                     logger.debug("Starting mic.");
                     setError('');
                     setInput('');
                     try {
                         recognitionRef.current.start();
                         setIsListening(true);
                     } catch (e) {
                         logger.error("Error starting recognition:", e);
                         setError(`Mic error: Could not start (${e.name || e.message})`);
                         setIsListening(false);
                     }
                 }
             })
             .catch(err => {
                 logger.error("Microphone access denied or error:", err);
                 setError("Microphone access denied. Please allow in browser settings.");
                 setIsListening(false);
             });
    };

    const handleToggleSpeaker = () => {
        setIsSpeakerOn(prevIsSpeakerOn => {
            const newIsSpeakerOn = !prevIsSpeakerOn;
            if (!newIsSpeakerOn && synthRef.current && synthRef.current.speaking) {
                logger.debug("Toggling speaker OFF: Stopping speech synthesis manually.");
                synthRef.current.cancel();
            }
            logger.debug(`Toggling speaker ${newIsSpeakerOn ? 'ON' : 'OFF'}`);
            return newIsSpeakerOn;
        });
    };

    if (typeof externallyControlledOpen === 'undefined') {
        logger.warn("AssistantWidget: 'externallyControlledOpen' prop is undefined. Widget rendering suppressed. This instance may be unintentionally globally instantiated. Please ensure 'externallyControlledOpen' (boolean) is passed from the parent component that controls its visibility, or remove the uncontrolled instance.");
        return null; 
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow:'hidden', bgcolor: 'background.default' }}>
            {(patientContext || episodeContext || contextType === 'physician_general_assistant') && (
                <Box sx={{ px: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'grey.100' }}>
                    <Chip
                        label={
                            contextType === 'physician_general_assistant' ? "General Assistant Mode" :
                            patientContext && episodeContext ? `Context: Pt ${String(patientContext).substring(0, 6)}... / Ep ${String(episodeContext).substring(0, 6)}...`
                            : patientContext ? `Context: Pt ${String(patientContext).substring(0, 6)}...`
                            : episodeContext ? `Context: Ep ${String(episodeContext).substring(0, 6)}...`
                            : "Context not set"
                        }
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: '100%' }}
                    />
                </Box>
            )}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
                <List dense sx={{ py: 0 }}>
                    {messages.map((msg, index) => {
                        const isUserMessage = msg.role === 'user';
                        return (
                            <ListItem key={index} sx={{ display: 'flex', justifyContent: isUserMessage ? 'flex-end' : 'flex-start', px: 0, py: 0.5 }}>
                                <Paper elevation={1} sx={{
                                    p: 1.2, maxWidth: '80%', wordWrap: 'break-word',
                                    bgcolor: isUserMessage ? (theme) => theme.palette.primary.lighter || alpha(theme.palette.primary.light, 0.1) : 'background.paper',
                                    color: 'text.primary',
                                    borderRadius: isUserMessage ? '15px 15px 0 15px' : '15px 15px 15px 0',
                                    opacity: loading && isUserMessage && index === messages.length -1 && (!messages[index-1] || messages[index-1]?.role === 'assistant') ? 0.6 : 1
                                }}>
                                    {renderAssistantResponse(msg.text)}
                                </Paper>
                            </ListItem>
                        );
                    })}
                    {loading && ( 
                        (messages.length === 0) || 
                        (messages.length > 0 && messages[messages.length -1]?.role === 'user') || 
                        (messages.length === 1 && (messages[0].text.toLowerCase().includes('generating') || messages[0].text.toLowerCase().includes('fetching') || messages[0].text.toLowerCase().includes('hello! how can i assist')))
                     ) && (
                        <ListItem sx={{ display: 'flex', justifyContent: 'flex-start', px: 0, py: 0.5 }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', p: 1, justifyContent: 'flex-start' }}>
                                 <CircularProgress size={18} sx={{ mr: 1 }} />
                                 <Typography variant="caption" sx={{ color: 'text.secondary' }}> Aria is thinking... </Typography>
                             </Box>
                        </ListItem>
                    )}
                    {error && !loading && (
                        <ListItem sx={{px: 0, justifyContent: 'center' }}>
                            <Alert severity="error" variant="filled" sx={{ width: '95%', fontSize: '0.8rem', p: '4px 10px', mt: 0.5 }} onClose={() => setError('')}>
                                {error}
                            </Alert>
                        </ListItem>
                    )}
                    <div ref={messagesEndRef} />
                </List>
            </Box>
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', backgroundColor: 'background.paper', flexShrink: 0 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Ask Aria or tap Mic..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(false); }}}
                    disabled={loading}
                    multiline
                    maxRows={4}
                    sx={{ mr: 0.5, '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                />
                <Tooltip title={isListening ? "Stop Listening" : "Speak Query (Mic Off)"}>
                     <span>
                        <IconButton onClick={handleToggleMic} color={isListening ? "error" : "default"} disabled={!recognitionRef.current || loading} aria-label="speak query">
                            {isListening ? <MicIcon /> : <MicOffIcon />}
                        </IconButton>
                     </span>
                </Tooltip>
                <Tooltip title="Send Message">
                    <span>
                        <IconButton color="primary" onClick={() => handleSend(false)} disabled={loading || !input.trim()} aria-label="send message">
                            {loading ? <CircularProgress size={24} /> : <SendIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
                 <Tooltip title={isSpeakerOn ? "Mute Responses" : "Read Responses Aloud"}>
                     <span>
                        <IconButton onClick={handleToggleSpeaker} color={isSpeakerOn ? "primary" : "default"} disabled={!synthRef.current} aria-label="toggle text to speech">
                            {isSpeakerOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
                        </IconButton>
                     </span>
                 </Tooltip>
            </Box>
        </Box>
    );
}

export default AssistantWidget;